<template>
  <!--
    The Analytic Bench (docs/teammate-analytic-report-panel-proposal.md §7) — GNey's analytics
    surface, and it lives IN the Dashboard page (user ruling 2026-08-23: no second rail, no popup).
    The page's tab row — Dashboards · Reports · My Insights — is the Bench's navigation; this
    component renders the Reports and My Insights panes. While it is on screen GNey runs in
    ANALYTICS mode (read-only, reports on request, §4g); leaving the tab returns it to operation
    mode. Chat-born insights route here too (Client.vue turns `gney:openReport` into ?report=).

    Rendering is fully client-side: GET api/teammate/reports/{id} hands back the stored program
    (bundle + page) and the data snapshot; FTSHtmlJsRenderer draws it in the same sandboxed frame the
    document viewer uses. No render-service dependency to VIEW — it is only a quality gate at
    generation (G1). Never an iframe in the chat column; the chat keeps a compact receipt card.
  -->
  <div class="ab-pane">
    <!-- Reports: the published workspace library (P2 §7c/§12d) — insights promoted with "Save to
         workspace". Every member sees the list; opening one resolves its rows LIVE under the
         viewer's own rights (never a stored snapshot), so two members can see different data. -->
    <div v-if="section === 'reports' && libView === 'list'" class="ab-list">
      <div class="ab-actions">
        <button type="button" class="ab-btn ab-btn--primary" title="Ask GNey — it is in analytics mode on this tab" @click="askGney">
          <font-awesome-icon :icon="['fas', 'chart-column']" /> <span>Ask GNey for a report</span>
        </button>
        <button type="button" class="ab-tab ab-tab--refresh" title="Reload" :disabled="libLoading" @click="loadLibrary">
          <font-awesome-icon :icon="['fas', 'sync-alt']" :spin="libLoading" />
        </button>
      </div>
      <div v-if="libError" class="ab-state ab-state--err">{{ libError }}</div>
      <div v-else-if="libLoading && !library.length" class="ab-state">Loading…</div>
      <div v-else-if="!library.length" class="ab-state">
        No published reports yet. Ask GNey for an insight, then save it to the workspace from My Insights{{ canPublish ? '' : ' (a workspace admin publishes it)' }} — it appears here for every member, with the data resolved under each viewer's own rights.
      </div>
      <div v-else class="ab-items">
        <div v-for="a in library" :key="a.id" class="ab-item" :class="{ 'is-on': analytic && analytic.id === a.id }" @click="openAnalytic(a.id)">
          <div class="ab-item-title">{{ a.name }}</div>
          <div class="ab-item-meta">{{ libraryLineOf(a) }}</div>
        </div>
        <button v-if="libHasMore" type="button" class="ab-btn ab-more" :disabled="libLoading" @click="loadMoreLibrary">
          {{ libLoading ? 'Loading…' : 'Load more' }}
        </button>
      </div>
    </div>

    <!-- One published report, in the page. -->
    <div v-else-if="section === 'reports'" class="ab-report">
      <div class="ab-report-head">
        <button type="button" class="ab-link ab-back" @click="showLibrary">
          <font-awesome-icon :icon="['fas', 'scroll']" /> Reports
        </button>
        <div class="ab-report-title" :title="analytic ? analytic.name : ''">
          <span>{{ analytic ? analytic.name : 'Report' }}</span>
          <button v-if="analytic && canManage" type="button" class="ab-icon-btn ab-icon-btn--inline" title="Rename" :disabled="libLoadingOne" @click="renameAnalytic">
            <font-awesome-icon :icon="['fas', 'pen-ruler']" />
          </button>
        </div>
        <div v-if="analytic" class="ab-report-scope">{{ libraryLineOf(analytic) }}</div>
        <div v-if="analytic && analytic.access && analytic.access.ok === false" class="ab-banner ab-banner--warn">
          No data for you in this report: {{ analytic.access.reason }}
        </div>
        <div v-else-if="analytic && analytic.truncated" class="ab-banner">
          Showing the first {{ analyticShownRows }} of {{ analytic.rowCount }} rows (row cap).
        </div>
        <div v-if="renderError" class="ab-banner ab-banner--warn">This report failed to render: {{ renderError }}</div>
      </div>
      <div class="ab-toolbar">
        <button type="button" class="ab-btn" :disabled="libLoadingOne || !canRenderAnalytic" title="Print / Save as PDF" @click="printAnalytic">
          <font-awesome-icon :icon="['fas', 'print']" /> <span>Print</span>
        </button>
        <button type="button" class="ab-btn" :disabled="libLoadingOne || !analytic" title="Re-resolve the data under your rights, as of now" @click="openAnalytic(analytic && analytic.id)">
          <font-awesome-icon :icon="['fas', 'sync-alt']" :spin="libLoadingOne" /> <span>Refresh</span>
        </button>
        <span class="ab-toolbar-spacer"></span>
        <span v-if="analytic" class="ab-asof" :title="analytic.resolvedAt">live · as of {{ formatWhen(analytic.resolvedAt) }}</span>
        <button v-if="canManage" type="button" class="ab-btn ab-btn--danger" :disabled="libLoadingOne || !analytic" title="Remove this report from the workspace library" @click="unpublish">Unpublish</button>
      </div>
      <div class="ab-view">
        <div v-if="libLoadingOne" class="ab-state">Loading report…</div>
        <div v-else-if="libOneError" class="ab-state ab-state--err">{{ libOneError }}</div>
        <fts-html-js-renderer
          v-else-if="canRenderAnalytic"
          ref="analyticRenderer"
          :bundle="analytic.bundle"
          :data="analytic.data"
          :page="analytic.page || {}"
          fit
          auto-height
          :min-height="400"
          @ready="onRendered"
        />
      </div>
    </div>

    <!-- My Insights: the owner's one-offs — this conversation's, or everything made here. -->
    <div v-else-if="view === 'list'" class="ab-list">
      <div class="ab-actions">
        <button type="button" class="ab-btn ab-btn--primary" title="Ask GNey — it is in analytics mode on this tab" @click="askGney">
          <font-awesome-icon :icon="['fas', 'chart-column']" /> <span>Ask GNey for an insight</span>
        </button>
        <button type="button" class="ab-tab ab-tab--refresh" title="Reload" :disabled="listLoading" @click="loadList">
          <font-awesome-icon :icon="['fas', 'sync-alt']" :spin="listLoading" />
        </button>
        <span class="ab-actions-sep"></span>
        <button type="button" class="ab-tab" :class="{ 'is-on': scope === 'session' }" :disabled="!sessionId" title="Only the insights from the conversation GNey has open" @click="setScope('session')">This chat</button>
        <button type="button" class="ab-tab" :class="{ 'is-on': scope === 'all' }" @click="setScope('all')">All my insights</button>
      </div>
      <div v-if="listError" class="ab-state ab-state--err">{{ listError }}</div>
      <div v-else-if="listLoading && !items.length" class="ab-state">Loading…</div>
      <div v-else-if="!items.length" class="ab-state">
        No insights yet. Ask GNey for a breakdown, a ranking or a comparison — it answers here as a report.
      </div>
      <div v-else class="ab-items">
        <div v-for="r in items" :key="r.id" class="ab-item" :class="{ 'is-on': report && report.id === r.id }" @click="openReport(r.id)">
          <div class="ab-item-title">
            <span>{{ r.title }}</span>
            <span v-if="r.publishedAnalyticId" class="ab-badge" title="Published to the workspace Reports library">Published</span>
          </div>
          <div class="ab-item-meta">{{ scopeLineOf(r) }}</div>
        </div>
        <button v-if="hasMore" type="button" class="ab-btn ab-more" :disabled="listLoading" @click="loadMore">
          {{ listLoading ? 'Loading…' : 'Load more' }}
        </button>
      </div>
    </div>

    <!-- One insight, in the page — never a modal or a rail. -->
    <div v-else class="ab-report">
      <div class="ab-report-head">
        <button type="button" class="ab-link ab-back" @click="showList">
          <font-awesome-icon :icon="['fas', 'clock-rotate-left']" /> My insights
        </button>
        <div class="ab-report-title" :title="report ? report.title : ''">
          <span>{{ report ? report.title : 'Report' }}</span>
          <button v-if="report" type="button" class="ab-icon-btn ab-icon-btn--inline" title="Rename" :disabled="loading" @click="rename">
            <font-awesome-icon :icon="['fas', 'pen-ruler']" />
          </button>
        </div>
        <div v-if="report" class="ab-report-scope">{{ scopeLineOf(report) }}</div>
        <div v-if="report && report.publishedAnalyticId" class="ab-banner">
          Published to the workspace Reports library{{ report.publishedAt ? ' on ' + formatWhen(report.publishedAt) : '' }}.
          <button type="button" class="ab-link" @click="openPublished(report.publishedAnalyticId)">Open in Reports</button>
        </div>
        <div v-if="publishNotice" class="ab-banner" :class="{ 'ab-banner--warn': publishNotice.warn }">{{ publishNotice.text }}</div>
        <div v-if="report && report.truncated" class="ab-banner">
          Showing the first {{ shownRows }} of {{ report.rowCount }} rows (row cap). Narrow the listing or filters for a complete picture.
        </div>
        <div v-if="report && report.snapshotDropped && !reportData" class="ab-banner ab-banner--warn">
          This report's data is too large to keep — ask GNey to narrow the projection or the filters.
        </div>
        <!-- G1: the runtime's fail signal (via the renderer's bridge) -> a way forward, not a blank frame. -->
        <div v-if="renderError" class="ab-banner ab-banner--warn">
          This report failed to render. <button type="button" class="ab-link" @click="regenerate">Ask GNey to fix it</button>
        </div>
      </div>
      <div class="ab-toolbar">
        <button type="button" class="ab-btn" :disabled="loading || !canRender" title="Print / Save as PDF" @click="print">
          <font-awesome-icon :icon="['fas', 'print']" /> <span>Print</span>
        </button>
        <button type="button" class="ab-btn" :disabled="loading || !report" title="Re-resolve the data under your rights, as of now" @click="refresh">
          <font-awesome-icon :icon="['fas', 'sync-alt']" :spin="refreshing" /> <span>Refresh</span>
        </button>
        <button type="button" class="ab-btn" :disabled="loading || !report" title="Ask GNey to change this report" @click="revise">
          <font-awesome-icon :icon="['fas', 'pen-ruler']" /> <span>Revise</span>
        </button>
        <!-- P2 promotion (§7c): admin-only button; the server re-checks the role. A non-admin sees
             no button — the Published banner / GNey's hand-off wording tell them an admin can. -->
        <button v-if="canPublish" type="button" class="ab-btn ab-btn--accent" :disabled="loading || publishing || !report"
          :title="report && report.publishedAnalyticId ? 'Publish this revision over the already-published report' : 'Publish to the workspace Reports library for every member'"
          @click="publish">
          <font-awesome-icon :icon="['fas', publishing ? 'spinner' : 'share-from-square']" :spin="publishing" />
          <span>{{ report && report.publishedAnalyticId ? 'Re-publish' : 'Save to workspace' }}</span>
        </button>
        <span class="ab-toolbar-spacer"></span>
        <span v-if="report" class="ab-asof" :title="report.refreshedAt">as of {{ formatWhen(report.refreshedAt) }}</span>
        <button type="button" class="ab-btn ab-btn--danger" :disabled="loading || !report" title="Delete this report" @click="remove">Delete</button>
      </div>
      <div class="ab-view">
        <div v-if="loading" class="ab-state">Loading report…</div>
        <div v-else-if="error" class="ab-state ab-state--err">{{ error }}</div>
        <fts-html-js-renderer
          v-else-if="canRender"
          ref="renderer"
          :bundle="report.bundle"
          :data="reportData"
          :page="report.page || {}"
          fit
          auto-height
          :min-height="400"
          @ready="onRendered"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { commonStore } from "../store/commonStore";
import { clientStore } from "../store/clientStore";
import { authorizationStore } from "../store/authorizationStore";
import FTSHtmlJsRenderer from "./FTSHtmlJsRenderer";

export default {
  name: "FTSAnalyticBench",
  components: { "fts-html-js-renderer": FTSHtmlJsRenderer },
  props: {
    /** Which pane the Dashboard page's tab row has selected: reports | insights. */
    section: { type: String, default: "insights" },
    /** Deep link: ?report=<id> — a chat report card routed here (Client.vue). */
    reportId: { type: String, default: "" },
    /** Deep link: ?analytic=<id> — a PUBLISHED report on the Reports tab (chat publish receipt). */
    analyticId: { type: String, default: "" },
  },
  data: () => ({
    view: "list",          // list | report
    scope: "all",          // all | session (the conversation GNey currently has open)
    sessionId: null,       // announced by the chat over the event bus
    items: [],
    listLoading: false,
    listError: null,
    hasMore: false,
    report: null,          // GET reports/{id} body
    loading: false,
    refreshing: false,
    error: null,
    // The runtime's verdict for the current render (from the renderer's `ready` event).
    renderError: null,
    // P2 promotion state (My Insights side).
    publishing: false,
    publishNotice: null,   // { text, warn } — transient outcome line under the title
    // Reports tab (the published library).
    libView: "list",       // list | report
    library: [],
    libLoading: false,
    libError: null,
    libHasMore: false,
    canManage: false,      // from the list endpoint: workspace admin
    analytic: null,        // GET analytics/{id} body (bundle + page + live data + access)
    libLoadingOne: false,
    libOneError: null,
  }),
  computed: {
    workspaceId() {
      return clientStore.getters.workspaceId;
    },
    reportData() {
      return this.report && this.report.dataSnapshot ? this.report.dataSnapshot : null;
    },
    canRender() {
      return !!(this.report && this.report.bundle && this.reportData);
    },
    shownRows() {
      const d = this.reportData;
      return d && Array.isArray(d.entries) ? d.entries.length : 0;
    },
    // Publish is a workspace-admin action (§12d); the server enforces it, this only shows the button.
    canPublish() {
      return !!authorizationStore.getters.isAdminUser;
    },
    canRenderAnalytic() {
      return !!(this.analytic && this.analytic.bundle && this.analytic.data);
    },
    analyticShownRows() {
      const d = this.analytic && this.analytic.data;
      return d && Array.isArray(d.entries) ? d.entries.length : 0;
    },
  },
  watch: {
    reportId(id) {
      if (id) this.openReport(id);
    },
    analyticId(id) {
      if (id) this.openAnalytic(id);
    },
    section(next) {
      // Leaving My Insights drops the open report so returning lands on the list.
      if (next !== "insights") this.view = "list";
      if (next === "reports") {
        if (this.analyticId) this.openAnalytic(this.analyticId);
        else { this.libView = "list"; this.loadLibrary(); }
      }
    },
  },
  mounted() {
    // Mode coupling (§4g): the Bench being on screen IS analytics mode. No rail, no popup — the
    // tab is the surface, so mount/destroy are the mode edges.
    window.eventBus.$emit("gney:benchState", { open: true });
    window.eventBus.$on("gney:session", this.onSession);
    // The chat mounts AFTER the page on a direct load (…?tab=insights), so it misses the announce
    // above; it asks on its own mount and we answer — otherwise GNey would sit in operation mode
    // while the Bench is on screen.
    window.eventBus.$on("gney:queryBenchState", this.announceBenchState);
    // The chat may already be running — ask it which session we're beside.
    window.eventBus.$emit("gney:querySession");
    if (this.section === "reports") {
      if (this.analyticId) this.openAnalytic(this.analyticId);
      else this.loadLibrary();
    } else if (this.reportId) this.openReport(this.reportId);
    else this.loadList();
  },
  beforeDestroy() {
    window.eventBus.$off("gney:session", this.onSession);
    window.eventBus.$off("gney:queryBenchState", this.announceBenchState);
    window.eventBus.$emit("gney:benchState", { open: false });
  },
  methods: {
    announceBenchState() {
      window.eventBus.$emit("gney:benchState", { open: true });
    },
    // The chat is already in analytics mode while this tab is open — just put the cursor in it.
    askGney() {
      window.eventBus.$emit("gney:composerPrefill", { focus: true });
    },

    /* ---------------- data ---------------- */
    requestConfig() {
      const wsId = this.workspaceId;
      return wsId ? { headers: { wsId } } : {};
    },
    onSession(evt) {
      const id = evt && evt.sessionId ? evt.sessionId : null;
      if (id === this.sessionId) return;
      this.sessionId = id;
      if (this.view === "list" && this.scope === "session") this.loadList();
    },
    setScope(scope) {
      if (this.scope === scope) return;
      this.scope = scope;
      this.loadList();
    },
    showList() {
      this.view = "list";
      this.loadList();
    },
    async loadList() {
      this.items = [];
      this.hasMore = false;
      await this.fetchPage(0);
    },
    loadMore() {
      return this.fetchPage(this.items.length);
    },
    // One page of the owner's reports (newest first); hasMore drives the Load more button.
    async fetchPage(skip) {
      this.listLoading = true;
      this.listError = null;
      try {
        const params = { limit: 50, skip };
        if (this.scope === "session" && this.sessionId) params.sessionId = this.sessionId;
        const res = await commonStore.state.client.get("/api/teammate/reports", { ...this.requestConfig(), params });
        const page = res.data && Array.isArray(res.data.reports) ? res.data.reports : [];
        this.items = skip > 0 ? this.items.concat(page) : page;
        this.hasMore = !!(res.data && res.data.hasMore);
      } catch (err) {
        this.listError = this.messageOf(err);
      } finally {
        this.listLoading = false;
      }
    },
    async rename() {
      if (!this.report) return;
      const next = window.prompt("Rename this report", this.report.title || "");
      const title = next == null ? "" : String(next).trim();
      if (!title || title === this.report.title) return;
      try {
        await commonStore.state.client.patch(`/api/teammate/reports/${encodeURIComponent(this.report.id)}`, { title }, this.requestConfig());
        this.report = Object.assign({}, this.report, { title });
        this.items = this.items.map((r) => (r.id === this.report.id ? Object.assign({}, r, { title }) : r));
      } catch (err) {
        this.error = this.messageOf(err);
      }
    },
    async openReport(id) {
      if (!id) return;
      this.view = "report";
      this.loading = true;
      this.error = null;
      this.renderError = null;
      try {
        const res = await commonStore.state.client.get(`/api/teammate/reports/${encodeURIComponent(id)}`, this.requestConfig());
        this.report = res.data || null;
        if (this.report && !this.report.dataSnapshot && !this.report.snapshotDropped) {
          // No stored snapshot (e.g. TTL'd per D4) — resolve live under the viewer's rights.
          await this.refresh();
        }
      } catch (err) {
        this.error = this.messageOf(err);
        this.report = null;
      } finally {
        this.loading = false;
      }
    },
    async refresh() {
      if (!this.report) return;
      this.refreshing = true;
      this.error = null;
      try {
        const res = await commonStore.state.client.post(`/api/teammate/reports/${encodeURIComponent(this.report.id)}/refresh`, null, this.requestConfig());
        this.report = res.data || this.report;
      } catch (err) {
        this.error = this.messageOf(err);
      } finally {
        this.refreshing = false;
      }
    },
    onRendered(evt) {
      this.renderError = evt && evt.ok === false ? (evt.error || "runtime error") : null;
    },
    // G1 affordance: hand the runtime error to GNey as a revision brief (the tool's revise path).
    regenerate() {
      if (!this.report) return;
      const detail = String(this.renderError || "").slice(0, 400);
      window.eventBus.$emit("gney:composerPrefill", {
        text: `The report "${this.report.title}" (report id ${this.report.id}) failed to render: ${detail}. Fix it and keep everything else.`,
      });
    },
    print() {
      if (this.$refs.renderer && typeof this.$refs.renderer.print === "function") {
        this.$refs.renderer.print(this.report ? this.report.title : "Report");
      }
    },
    revise() {
      if (!this.report) return;
      // The round trip stays a conversation: pre-fill the composer so the user states the change
      // and GNey revises by reportId (the tool's revise path).
      window.eventBus.$emit("gney:composerPrefill", {
        text: `Revise the report "${this.report.title}" (report id ${this.report.id}): `,
      });
    },
    async remove() {
      if (!this.report) return;
      if (!window.confirm(`Delete the report "${this.report.title}"?`)) return;
      try {
        await commonStore.state.client.delete(`/api/teammate/reports/${encodeURIComponent(this.report.id)}`, this.requestConfig());
        this.report = null;
        this.showList();
      } catch (err) {
        this.error = this.messageOf(err);
      }
    },

    /* ---------------- P2 promotion: Save to workspace ---------------- */
    async publish() {
      if (!this.report || this.publishing) return;
      const republish = !!this.report.publishedAnalyticId;
      const verb = republish ? "Re-publish" : "Publish";
      if (!window.confirm(`${verb} "${this.report.title}" to the workspace Reports library? Every workspace member will be able to open it, with the data resolved under their own rights.`)) return;
      this.publishing = true;
      this.publishNotice = null;
      try {
        const res = await commonStore.state.client.post(`/api/teammate/reports/${encodeURIComponent(this.report.id)}/publish`, {}, this.requestConfig());
        const a = res.data || {};
        this.report = Object.assign({}, this.report, { publishedAnalyticId: a.id, publishedAt: a.publishedAt });
        this.items = this.items.map((r) => (r.id === this.report.id ? Object.assign({}, r, { publishedAnalyticId: a.id, publishedAt: a.publishedAt }) : r));
        this.publishNotice = { text: republish ? "Published report updated." : "Published — every workspace member can open it under Reports." };
        // The library list is stale now; it reloads on the next visit to the tab.
        this.library = [];
      } catch (err) {
        this.publishNotice = { text: this.messageOf(err), warn: true };
      } finally {
        this.publishing = false;
      }
    },
    openPublished(id) {
      if (id) window.eventBus.$emit("gney:openAnalytic", { id });
    },

    /* ---------------- Reports tab: the published library ---------------- */
    showLibrary() {
      this.libView = "list";
      this.analytic = null;
      this.loadLibrary();
    },
    async loadLibrary() {
      this.library = [];
      this.libHasMore = false;
      await this.fetchLibraryPage(0);
    },
    loadMoreLibrary() {
      return this.fetchLibraryPage(this.library.length);
    },
    async fetchLibraryPage(skip) {
      this.libLoading = true;
      this.libError = null;
      try {
        const res = await commonStore.state.client.get("/api/teammate/analytics", { ...this.requestConfig(), params: { kind: "report", limit: 50, skip } });
        const page = res.data && Array.isArray(res.data.analytics) ? res.data.analytics : [];
        this.library = skip > 0 ? this.library.concat(page) : page;
        this.libHasMore = !!(res.data && res.data.hasMore);
        this.canManage = !!(res.data && res.data.canManage);
      } catch (err) {
        this.libError = this.messageOf(err);
      } finally {
        this.libLoading = false;
      }
    },
    // Opening = resolving: the rows come back resolved live under the viewer's rights.
    async openAnalytic(id) {
      if (!id) return;
      this.libView = "report";
      this.libLoadingOne = true;
      this.libOneError = null;
      this.renderError = null;
      try {
        const res = await commonStore.state.client.get(`/api/teammate/analytics/${encodeURIComponent(id)}`, this.requestConfig());
        this.analytic = res.data || null;
        if (this.analytic && typeof this.analytic.canManage === "boolean") this.canManage = this.analytic.canManage;
      } catch (err) {
        this.libOneError = this.messageOf(err);
        this.analytic = null;
      } finally {
        this.libLoadingOne = false;
      }
    },
    async renameAnalytic() {
      if (!this.analytic) return;
      const next = window.prompt("Rename this report", this.analytic.name || "");
      const title = next == null ? "" : String(next).trim();
      if (!title || title === this.analytic.name) return;
      try {
        await commonStore.state.client.patch(`/api/teammate/analytics/${encodeURIComponent(this.analytic.id)}`, { title }, this.requestConfig());
        this.analytic = Object.assign({}, this.analytic, { name: title, title });
        this.library = this.library.map((a) => (a.id === this.analytic.id ? Object.assign({}, a, { name: title, title }) : a));
      } catch (err) {
        this.libOneError = this.messageOf(err);
      }
    },
    async unpublish() {
      if (!this.analytic) return;
      if (!window.confirm(`Remove "${this.analytic.name}" from the workspace Reports library? The original insight stays in its owner's My Insights.`)) return;
      try {
        await commonStore.state.client.delete(`/api/teammate/analytics/${encodeURIComponent(this.analytic.id)}`, this.requestConfig());
        this.analytic = null;
        this.showLibrary();
      } catch (err) {
        this.libOneError = this.messageOf(err);
      }
    },
    printAnalytic() {
      if (this.$refs.analyticRenderer && typeof this.$refs.analyticRenderer.print === "function") {
        this.$refs.analyticRenderer.print(this.analytic ? this.analytic.name : "Report");
      }
    },
    libraryLineOf(a) {
      if (!a) return "";
      const parts = [];
      if (a.module) parts.push(a.module);
      if (a.listingName) parts.push(a.listingName);
      if (typeof a.rowCount === "number" && a.data) parts.push(`${a.rowCount} row${a.rowCount === 1 ? "" : "s"} for you`);
      if (a.publishedBy) parts.push(`published by ${a.publishedBy}`);
      if (a.publishedAt) parts.push(this.formatWhen(a.publishedAt));
      return parts.join(" · ");
    },

    /* ---------------- presentation ---------------- */
    scopeLineOf(r) {
      if (!r) return "";
      const parts = [];
      if (r.module) parts.push(r.module);
      if (r.listingName) parts.push(r.listingName);
      if (typeof r.rowCount === "number") parts.push(`${r.rowCount} row${r.rowCount === 1 ? "" : "s"}`);
      if (r.refreshedAt) parts.push(`as of ${this.formatWhen(r.refreshedAt)}`);
      return parts.join(" · ");
    },
    formatWhen(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return String(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    },
    messageOf(err) {
      const d = err && err.response && err.response.data;
      if (d) {
        if (typeof d === "string") return d;
        if (d.message || d.error) return d.message || d.error;
      }
      return (err && err.message) || "Something went wrong. Please try again.";
    },
  },
};
</script>

<style lang="scss" scoped>
/* Page content, not a rail: no fixed positioning, no body gutters, no overlay — the Dashboard page
   owns the layout and the tab row above owns the navigation. */
.ab-pane { font-size: 13px; color: var(--tm-text, #1f2937); }

.ab-list { display: flex; flex-direction: column; }
.ab-actions { display: flex; align-items: center; gap: 4px; padding-bottom: 10px; }
.ab-actions-sep { width: 1px; align-self: stretch; margin: 2px 8px; background: rgba(0, 0, 0, 0.12); }
.ab-tab {
  border: none; background: transparent; cursor: pointer; padding: 5px 10px; border-radius: 999px;
  font-size: 12px; color: var(--tm-text-muted, #6b7280);
}
.ab-tab:hover:not(:disabled) { background: rgba(0, 0, 0, 0.04); }
.ab-tab:disabled { opacity: 0.45; cursor: default; }
.ab-tab.is-on { background: rgba(79, 70, 229, 0.1); color: var(--tm-accent, #4f46e5); font-weight: 600; }
.ab-tab--refresh { flex-shrink: 0; }
.ab-items { display: flex; flex-direction: column; gap: 6px; }
.ab-item { background: #fff; border-radius: 10px; padding: 10px 12px; cursor: pointer; box-shadow: 0 0 5px rgba(0, 0, 0, 0.06); }
.ab-item:hover, .ab-item.is-on { outline: 2px solid rgba(79, 70, 229, 0.35); }
.ab-item-title { font-weight: 600; display: flex; align-items: center; gap: 8px; }
.ab-badge {
  font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .03em;
  padding: 1px 7px; border-radius: 999px; background: rgba(5, 150, 105, 0.12); color: #047857;
}
.ab-item-meta { font-size: 11.5px; color: var(--tm-text-muted, #6b7280); margin-top: 2px; }
.ab-state { padding: 6px 0 18px; color: var(--tm-text-muted, #6b7280); line-height: 1.5; }
.ab-state--err { color: var(--tm-danger, #b91c1c); }

/* The open insight: one card in the page flow. */
.ab-report {
  background: #fff; border: 1px solid rgba(0, 0, 0, 0.06); border-radius: 12px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.06); overflow: hidden;
}
.ab-report-head { padding: 10px 14px 6px; background: #fff; }
.ab-back { display: inline-flex; align-items: center; gap: 6px; color: var(--tm-accent, #4f46e5); text-decoration: none; font-size: 12px; margin-bottom: 6px; }
.ab-back:hover { text-decoration: underline; }
.ab-report-title { font-weight: 700; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: flex; align-items: center; gap: 4px; }
.ab-report-title > span { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.ab-icon-btn {
  border: none; background: transparent; cursor: pointer; width: 30px; height: 30px; border-radius: 8px;
  color: var(--tm-text-muted, #6b7280);
}
.ab-icon-btn:hover { background: rgba(0, 0, 0, 0.06); color: var(--tm-text, #1f2937); }
.ab-icon-btn--inline { width: 24px; height: 24px; font-size: 11px; flex-shrink: 0; }
.ab-more { align-self: center; margin-top: 4px; }
.ab-report-scope { font-size: 11.5px; color: var(--tm-text-muted, #6b7280); margin-top: 2px; }
.ab-banner {
  margin-top: 8px; padding: 6px 10px; border-radius: 8px; font-size: 12px;
  background: rgba(79, 70, 229, 0.08); color: var(--tm-accent, #4f46e5);
}
.ab-banner--warn { background: rgba(217, 119, 6, 0.1); color: #b45309; }
.ab-toolbar {
  display: flex; align-items: center; gap: 6px; padding: 6px 10px;
  background: #fff; border-top: 1px solid rgba(0, 0, 0, 0.05); border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.ab-toolbar-spacer { flex: 1; }
.ab-asof { font-size: 11px; color: var(--tm-text-muted, #6b7280); margin-right: 6px; }
.ab-btn {
  display: inline-flex; align-items: center; gap: 6px; border: 1px solid rgba(0, 0, 0, 0.1);
  background: #fff; border-radius: 8px; padding: 5px 10px; font-size: 12px; cursor: pointer;
  color: var(--tm-text, #1f2937);
}
.ab-btn:hover:not(:disabled) { background: rgba(0, 0, 0, 0.04); }
.ab-btn:disabled { opacity: 0.5; cursor: default; }
.ab-btn--danger { color: var(--tm-danger, #b91c1c); }
.ab-btn--accent { color: var(--tm-accent, #4f46e5); border-color: rgba(79, 70, 229, 0.35); font-weight: 600; }
.ab-btn--primary { background: var(--tm-accent, #4f46e5); border-color: transparent; color: #fff; font-weight: 600; }
.ab-btn--primary:hover:not(:disabled) { background: var(--tm-accent, #4f46e5); filter: brightness(1.08); }
.ab-view { padding: 10px; }
.ab-link { border: none; background: transparent; color: inherit; text-decoration: underline; cursor: pointer; font: inherit; padding: 0; }
</style>
